/**
 * English strings of the application shell (W1-11b), namespace 'shell': top bar, transport, snap, status chips,
 * banners, hint bar, toasts, palette, cheat sheet, dialogs host, crash screens, layouts and the shell's commands.
 * zh-CN lives in src/i18n/zh-CN/shell.ts (localisation package). No HTML; ICU-lite syntax (src/i18n/icu.ts).
 * Select branch names use [A-Za-z0-9_] only: ids with dots are passed through `selectKey` ('sel.any' -> 'sel_any',
 * src/app/shell/selectKey.ts). shell.messages.test.ts formats every message with the real runtime.
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('shell', {
  // ---- workspaces and layout presets ----
  wsTiming: 'Song & Timing',
  wsTimingShort: 'Timing',
  wsChart: 'Chart',
  wsPublish: 'Publish',
  wsLibrary: 'Library',
  wsSettings: 'Settings',
  layoutCompose: 'Compose',
  layoutComposeDetail: 'Note lane and motion columns, a large preview with the panels under it.',
  layoutAnimate: 'Animate',
  layoutAnimateDetail: 'More room for the motion columns; a smaller preview.',
  layoutReview: 'Review',
  layoutReviewDetail: 'The largest preview, with the Problems tab open.',
  layoutLaptop: 'Laptop',
  layoutLaptopDetail: 'For small screens: the preview floats and the panels move into a side drawer.',
  layoutAuto: 'Laptop layout (applied automatically on this screen size)',

  // ---- command titles ----
  cmdPalette: 'Command palette',
  cmdWorkspace: 'Switch workspace',
  cmdCloseProject: 'Close the project',
  cmdLibrary: 'Go to the Library',
  cmdUndo: 'Undo',
  cmdRedo: 'Redo',
  cmdZoomIn: 'Zoom time in',
  cmdZoomOut: 'Zoom time out',
  cmdResetView: 'Reset the timeline view',
  cmdResetLanePan: 'Centre the note lane (reset the X pan)',
  cmdFrameSelection: 'Frame the selection',
  cmdMotionColumns: 'Show motion columns',
  cmdWaveform: 'Show the waveform',
  cmdFollowPlayhead: 'Follow the playhead',
  cmdLayout: 'Layout',
  cmdMiniPreview: 'Mini preview window',
  cmdDrawer: 'Side drawer',
  cmdPlayPause: 'Play / pause',
  cmdPlayFromStart: 'Play from the chart start',
  cmdHoldReturn: 'Play while held, then go back',
  cmdHoldStay: 'Play while held, then stay',
  cmdStopReturn: 'Stop and go back to where playback started',
  cmdStopStay: 'Stop here',
  cmdPlayMaximized: 'Play with the preview maximised',
  cmdReturnOnStop: 'Go back to the start point on stop',
  cmdRateDown: 'Slower playback',
  cmdRateUp: 'Faster playback',
  cmdSetRate: 'Playback speed',
  cmdMetronome: 'Metronome',
  cmdLoop: 'Loop playback',
  cmdSeekForward: 'Forward one snap step',
  cmdSeekBack: 'Back one snap step',
  cmdSeekBarForward: 'Forward one bar',
  cmdSeekBarBack: 'Back one bar',
  cmdSeekStart: 'Go to the chart start',
  cmdSeekEnd: 'Go to the chart end',
  cmdSeekSelStart: 'Go to the start of the selection',
  cmdSeekSelEnd: 'Go to the end of the selection',
  cmdSeekSeconds: 'Move the playhead by seconds',
  cmdSnapPlayhead: 'Snap the playhead to the grid',
  cmdSnapDiv: 'Set beat snap',
  cmdSnapFiner: 'Finer beat snap',
  cmdSnapCoarser: 'Coarser beat snap',
  cmdSnapBeat: 'Snap to the beat grid',
  cmdSnapX: 'Snap to the X grid',
  cmdSnapXGrid: 'Set the number of X grid lines',
  cmdGoto: 'Go to a beat, time or marker',
  cmdReveal: 'Show a position in the timeline',
  cmdPrevKey: 'Previous keyframe',
  cmdNextKey: 'Next keyframe',
  cmdPrevNote: 'Previous note on the active line',
  cmdNextNote: 'Next note on the active line',
  cmdHelpPalette: 'Search help topics',
  cmdCheatSheet: 'Keyboard shortcuts',

  // ---- command arguments ----
  argWorkspace: 'Workspace',
  argLayout: 'Layout',
  argRate: 'Playback speed',
  argSeconds: 'Seconds (negative goes back)',
  argSnapDiv: 'Divide each beat into (1 to 1920)',
  argXGrid: 'X grid lines (1 to 1000)',
  rateOption: '{rate}x',
  argAnyProperty: 'any animated property',
  argSecondsValue: '{sec, number, 0.0##} s',
  argSteps: '{n, plural, one {# step} other {# steps}}',
  argMs: '{n} ms',
  argQuick: 'without the dialog',

  // ---- disabled reasons ----
  reasonNoProject: 'Open a chart first.',
  reasonNothingToUndo: 'Nothing to undo.',
  reasonNothingToRedo: 'Nothing to redo.',
  reasonGestureActive: 'Finish or cancel the current drag first.',
  reasonNoSelection: 'Select notes or keyframes first.',
  reasonNoFocusedColumn: 'Click a motion column or pick a line first.',
  reasonNoActiveLine: 'Pick a line first.',
  reasonLaptopOnly: 'Only used by the Laptop layout (small screens).',
  reasonGuidedDocked: 'In the Guided level the preview always stays in view.',
  reasonNarrowDocked: 'On a narrow screen the preview stays docked next to the note lane.',
  reasonNotHere: 'Not available here.',
  reasonUnknownCommand: 'This command is not part of this version.',

  // ---- toasts raised by the shell ----
  toastUndid: 'Undid: {label}',
  toastRedid: 'Redid: {label}',
  toastTip: 'Tip: "{command}" also has a shortcut: {keys}',
  toastDialogUnavailable: 'This window is not available in this version yet.',
  toastNoNoteBefore: 'No earlier note on this line.',
  toastNoNoteAfter: 'No later note on this line.',
  toastNoKeyBefore: 'No earlier keyframe.',
  toastNoKeyAfter: 'No later keyframe.',
  toastError: 'Something went wrong: {error}',
  toastDropNone: 'Phiedit cannot open {count, plural, one {this file} other {these files}}.',
  toastDropFailed: 'Could not open the dropped files: {error}',
  toastDropEmpty: 'The dropped folder has no files to open.',
  toastDropTruncated: 'The dropped folders hold too many files; only the first {count} were read.',
  toastRecoveryFailed: 'The recovery data could not be read: {error}',
  toastProtected: 'Your charts are now protected from automatic cleanup.',
  toastProtectRefused: 'The browser did not allow it. Download a backup now and then to be safe.',
  toastCopied: 'Copied to the clipboard.',

  // ---- toasts UI ----
  toastsLabel: 'Notifications',
  toastUndo: 'Undo',
  toastDismiss: 'Dismiss',
  toastCount: 'x{count}',
  toastKind: '{kind, select, success {Done} warning {Warning} error {Error} other {Note}}',

  // ---- composition ----
  labelValue: '{label}: {value}',
  /** A binding row's named choice ("Switch workspace (Chart)"): parentheses, never "workspace: ..." (UX-010). */
  argParen: '{label} ({value})',
  withArgs: '{title}...',
  orJoin: '{a} / {b}',
  listJoin: '{a}, {b}',
  projectMenuLabel: 'Project menu: {name}{level, select, none {} other { ({level})}}',
  reportComponentStack: 'Component stack:',
  reportUrl: 'Address: {url}',
  reportBrowser: 'Browser: {agent}',
  reportTime: 'Time: {time}',

  // ---- top bar ----
  topBarLabel: 'Editor toolbar',
  workspacesLabel: 'Workspaces',
  overflow: 'More',
  overflowAttention: 'More (something needs your attention)',
  paletteButton: 'Search commands',
  undoWith: 'Undo: {label}',
  redoWith: 'Redo: {label}',
  projectMenu: 'Project menu',
  untitled: 'Untitled chart',
  menuEdit: 'Edit',
  menuView: 'View',
  menuHelp: 'Help',
  menuSnap: 'Snap',

  // ---- transport ----
  transportLabel: 'Playback',
  play: 'Play',
  pause: 'Pause',
  readoutApprox: '~{beat}',
  readoutExact: 'Exactly {beat} (rounded to the snap in the readout)',
  timeReadout: 'Playhead at beat {beat}, {time}. Click to type a position.',
  timeInputLabel: 'Go to a beat or time',
  timeInputPlaceholder: '12:1/4, 23.5s or m1',
  timeInvalid: 'Not a beat or time this field understands.',
  rateLabel: 'Playback speed {rate}x',
  rateStreaming: 'Slowed playback keeps the pitch; timing is slightly less exact.',

  // ---- snap ----
  snapLabel: 'Snap',
  snapDivLabel: 'Beat snap',
  snapDivValue: '1/{div}',
  snapDivTriplet: '1/{div} (triplets)',
  snapCustomLabel: 'Divide each beat into',
  snapApply: 'Apply',
  xGridLabel: 'X grid lines',
  xGridValue: 'X {n}',

  // ---- status chips ----
  saveSaved: 'Saved',
  saveSaving: 'Saving...',
  saveError: 'Not saved',
  saveErrorFull: 'Not saved: storage full',
  saveReconnecting: 'Reconnecting to storage...',
  saveAtRisk: '{risk, select, lowSpace {At risk: storage almost full} other {At risk: not protected}}',
  saveDetailsTitle: 'Saving',
  saveLastSaved: 'Last saved {time}.',
  saveNeverSaved: 'Nothing saved in this session yet.',
  savePending: '{count, plural, one {# change is} other {# changes are}} waiting to be written.',
  saveUsage: '{used} of {quota} of browser storage used ({free} free).',
  saveProtected: 'Protected from automatic cleanup.',
  saveNotProtected: 'Not protected: the browser may delete charts when space runs low.',
  saveWriteError: 'Last write failed: {error}',
  protectCharts: 'Protect my charts',
  downloadRecovery: 'Download recovery data',
  downloadBackup: 'Download backup now',
  problemsChip:
    '{fatal, plural, =0 {} other {# fatal, }}{errors, plural, one {# error} other {# errors}}, {warnings, plural, one {# warning} other {# warnings}}{tips, plural, =0 {} one {, # tip} other {, # tips}}. Click to show them.',
  problemsNone: 'No problems found. Click to open Problems.',
  problemsChecking: 'Checking the chart. Problems appear as soon as the first check is done. Click to open Problems.',
  problemsCheckingShort: 'Checking…',
  levelLabel: 'Experience level: {level, select, guided {Guided} other {Full}}',
  levelGuided: 'Guided',
  levelGuidedDetail: 'Fewer tools at once, plain-word hints.',
  levelFull: 'Full',
  levelFullDetail: 'Every tool and property.',
  exportChanged: 'Changed since the last export ({when}). Click to export.',
  exportNever: 'Not exported yet. Click to export.',
  exportChip: 'Changed since export',
  bytes: '{n, number, 0.#} {unit, select, kb {KB} mb {MB} gb {GB} other {B}}',
  timeAgoNow: 'just now',
  timeAgoMinutes: '{n, plural, one {# minute} other {# minutes}} ago',
  timeAgoHours: '{n, plural, one {# hour} other {# hours}} ago',
  timeAgoDays: '{n, plural, one {# day} other {# days}} ago',

  // ---- banners ----
  bannersLabel: 'Notices',
  bannerWriteError: 'Changes are not being saved: {error}',
  bannerQuota:
    'Browser storage is almost full: {free} left ({used} of {quota} used). Download a backup now; deleting old charts or versions frees space.',
  bannerNotPersisted: 'Your browser may delete charts when space runs low. Download a backup now and then to be safe.',
  bannerExportStale:
    'This chart changed after its last export {when}. Export a copy to keep it safe outside the browser.',
  bannerExportNever:
    'This chart was started {when} and has never been exported. Export a copy to keep it safe outside the browser.',
  exportNow: 'Export...',
  bannerReconnecting: 'Reconnecting to storage. Your edits are kept and written as soon as it is back.',
  dismiss: 'Dismiss',
  noticeCount: '{index} of {count}',
  noticeNext: 'Next notice ({index} of {count})',
  noticeLater: 'Later',
  noticeLaterDetail: 'Hide this notice for now. It comes back after about two hours of editing.',

  // ---- hint bar ----
  hintBarLabel: 'Hints',
  hintIn: '{where}:',
  hintRegion:
    '{region, select, lane {Note lane} motion {Motion columns} curve {Curve editor} preview {Preview} lines {Lines} inspector {Inspector} timing {Song & Timing} dock {Dock} problems {Problems} history {History} assets {Assets} raw {Raw} publish {Publish} library {Library} settings {Settings} other {Editor}}',
  hintTool:
    '{tool, select, select {Select tool} tap {Tap tool} drag {Drag tool} flick {Flick tool} hold {Hold tool} eraser {Eraser tool} other {{tool}}}',
  hintGestureItem: '{keys} {label}',
  hintToolUse:
    '{tool, select, tap {Click in the note lane to place a Tap.} drag {Click in the note lane to place a Drag.} flick {Click in the note lane to place a Flick.} hold {Drag up in the note lane to draw a Hold.} eraser {Click a note to erase it, or drag over several.} other {}}',
  hintFocusedColumn:
    'Keyframes go to {prop, select, moveX {position X} moveY {position Y} rotate {rotation} alpha {opacity} speed {scroll speed} scaleX {scale X} scaleY {scale Y} color {colour} text {text} incline {note tilt} paint {paint} gif {GIF progress} other {{prop}}} of {line}{layer, plural, =0 {} other { (layer #)}}.',
  hintNoProject: 'Open a chart or start a new one.',
  hintCheatSheet: 'Keyboard shortcuts',

  // ---- palette ----
  paletteLabel: 'Command palette',
  palettePlaceholder: 'Type a command, or # line, @ time, ? help, : setting',
  palettePlaceholderLines: 'Line number or name',
  palettePlaceholderTime: 'Beat (12:1/4), time (1:23.5) or marker name',
  palettePlaceholderHelp: 'Search help topics and the glossary',
  palettePlaceholderSettings: 'Search settings',
  palettePlaceholderQuery: 'Select by query',
  paletteEmpty: 'No matches.',
  paletteNothingYet: 'Nothing to search here in this version.',
  paletteLoading: 'Searching...',
  paletteRecent: 'Recent',
  paletteCommands: 'Commands',
  paletteResults: 'Results',
  paletteNotHereGroup: 'Not available here',
  paletteFullTag: 'Full',
  paletteFullTagHint: 'A Full-level command: you can run it once or switch to Full.',
  palettePrefixes: 'Prefixes',
  prefixCommands: 'commands',
  prefixLines: 'lines',
  prefixTime: 'time or marker',
  prefixQuery: 'query',
  prefixHelp: 'help',
  prefixSettings: 'settings',
  paletteArgBack: 'Back to commands',
  paletteArgHint: 'Enter runs it, Esc goes back.',
  paletteArgNumber: 'Enter a number from {min} to {max}.',
  paletteArgNumberAny: 'Enter a number.',
  paletteArgBeatInvalid: 'Not a beat this field understands.',
  paletteArgBeatEcho: '= {beat}',
  paletteArgNoLines: 'Open a chart to pick a line.',
  paletteArgLine: 'Line {index}: {name}',
  paletteArgUnnamedLine: 'Line {index}',
  paletteGotoBeat: 'Go to beat {beat}',
  paletteGotoRelative: 'Move the playhead to beat {beat}',
  paletteGotoTime: 'Go to {time}',
  paletteGotoDetail: '{beat} · {time}',
  paletteMarker: 'Marker: {name}',
  category:
    '{category, select, File {File} Edit {Edit} Select {Select} View {View} Playback {Playback} Notes {Notes} Motion {Motion} Lines {Lines} Timing {Timing} Tools {Tools} Publish {Publish} Help {Help} other {{category}}}',

  // ---- cheat sheet ----
  cheatTitle: 'Keyboard shortcuts',
  cheatPreset: '{preset, select, rpe {RPE keys} other {Default keys}}{altFree, select, yes {, Alt-free} other {}}',
  cheatSearch: 'Search shortcuts',
  cheatPrint: 'Print',
  cheatEmpty: 'No shortcut matches.',
  cheatActiveNow: 'Works now',
  cheatEverywhere: 'Everywhere',
  cheatEdit: 'Change shortcuts',
  cheatNot: 'not {what}',
  /** A negated selection kind ("no notes selected", not "not notes selected"; UX-010). Same parameters as `atom`. */
  cheatNone:
    '{atom, select, sel_notes {no notes selected} sel_keys {no keyframes selected} sel_segments {no segments selected} sel_lines {no lines selected} sel_bpm {no tempo points selected} sel_any {nothing selected} sel_single {not one item selected} sel_mixed {not a mixed selection} other {not {name}}}',
  cheatJoin: '{a}, {b}',
  cheatSituations: '{a}; {b}',
  cheatMouse: 'Mouse and trackpad',
  gestureWheel: 'Scroll the timeline',
  gestureShiftWheel: 'Scroll the timeline faster (not on a Mac)',
  gestureCtrlWheel: 'Zoom time at the pointer (a trackpad pinch too)',
  gestureCtrlWheelRpe:
    'Fine-tune what is under the pointer: the width of the selected notes, a keyframe value (a trackpad pinch zooms time)',
  gestureAltWheel: 'Change the beat snap',
  gestureAltWheelRpe: 'Scroll the timeline faster',
  gestureCtrlShiftWheel: 'Zoom the column under the pointer sideways',
  gestureMiddleDrag: 'Pan the timeline',
  gestureMiddleDragKeys: 'Middle-button drag',
  atom: '{atom, select, textInput {typing in a field} dialog {in a dialog} paletteOpen {palette open} placing_hold {placing a Hold} placing_segment {placing a segment} paste_ghost {pasting} gesture {while dragging} playing {while playing} sel_notes {notes selected} sel_keys {keyframes selected} sel_segments {segments selected} sel_lines {lines selected} sel_bpm {tempo points selected} sel_any {something selected} sel_single {one item selected} sel_mixed {different kinds selected} column_focused {a motion column focused} project_open {a chart open} activeLine_uiMover {on a UI mover line} activeLine_notesAllowed {on a line with notes} testPlay {in a test play} theater {in theater mode} batch_sheetOpen {a tool sheet open} problems_fixing {fixing a problem} curve_expanded {the curve editor open} markers_selected {markers selected} tour_active {during the tour} help_focused {in the help panel} timing_overviewHover {pointer over the song overview} other {when {name} applies}}',
  atomRegion:
    '{prefix, select, hover {pointer over} other {in}} {region, select, lane {the note lane} motion {the motion columns} curve {the curve editor} preview {the preview} lines {the Lines panel} inspector {the inspector} timing {Song & Timing} dock {the dock} problems {Problems} history {History} assets {Assets} raw {the Raw panel} publish {Publish} library {the Library} settings {Settings} other {{region}}}',
  atomCompare:
    '{name, select, workspace {workspace} level {level} preset {key preset} tool {tool} os {system} other {{name}}} {op, select, ne {is not} other {is}} {value}',

  // ---- context menus, dialogs ----
  contextMenuLabel: 'Context menu',
  dialogCancel: 'Cancel',
  dialogCrashed: 'This window stopped working and was closed.',
  chooseHint: 'Pick one',
  popoverLabel: 'Pop-up',

  // ---- crash screens ----
  crashTitle: 'Something went wrong',
  crashBody:
    'Phiedit hit an unexpected error. Your chart is saved up to the last change; reloading the page usually fixes this.',
  crashReload: 'Reload',
  crashLibrary: 'Go to the Library',
  crashDownload: 'Download recovery data',
  crashCopy: 'Copy error details',
  crashDetails: 'Error details',
  panelCrashTitle: 'This panel stopped working',
  panelCrashBody: 'The rest of the editor still works. Reloading the panel usually fixes it.',
  panelRetry: 'Reload the panel',
  bootFailedTitle: 'Phiedit could not start',
  bootFailedBody: 'Something needed at startup failed: {error}',

  // ---- slots and layout ----
  slotEmptyTitle: 'Nothing here yet',
  slotEmpty:
    '{slot, select, chart_preview {The preview of the chart appears here.} chart_timeline {The note lane and the motion columns appear here.} chart_inspector {Select something to see its properties here.} chart_dock {Lines, Problems and History appear here.} chart_tools {The tools appear here.} timing_left {The tempo and the song offset appear here.} timing_main {The song overview appears here.} timing_bottom {The alignment tools appear here.} publish_left {The chart check appears here.} publish_right {Packaging and export appear here.} other {This part of the editor is not available in this version yet.}}',
  slotLabel:
    '{slot, select, chart_tools {Tools} chart_timeline {Timeline} chart_preview {Preview} chart_dock {Dock} chart_inspector {Inspector} timing_left {Tempo and offset} timing_main {Song overview} timing_bottom {Alignment} publish_left {Check} publish_right {Package and export} topbar_left {Project tools} topbar_center {Playback tools} topbar_right {Status} statusbar {Status bar} other {Panel}}',
  sectionToggle: '{open, select, yes {Collapse} other {Expand}} {title}',
  miniPreviewTitle: 'Preview',
  miniPreviewHide: 'Hide the preview',
  miniPreviewMove: 'Drag to move the preview',
  drawerTitle: 'Panels',
  drawerClose: 'Close the drawer',
  /** Bottom drawer of the motion columns on narrow screens (below 1024 px; W3-01, CR-W2-04a-7). */
  motionDrawerTitle: 'Motion',
  motionDrawerLabel: 'Motion columns (bottom drawer)',
  workspaceLabel: '{workspace, select, timing {Song & Timing} publish {Publish} other {Chart}} workspace',

  // ---- editor shell, router, drop zone ----
  editorLabel: 'Chart editor',
  opening: 'Opening the chart...',
  openFailedTitle: 'The chart could not be opened',
  openFailedBody: '{error}',
  backToLibrary: 'Back to the Library',
  retry: 'Try again',
  notFoundTitle: 'Page not found',
  notFoundBody: 'The address {hash} does not lead anywhere in Phiedit.',
  libraryUnavailableTitle: 'The Library is not available in this version yet',
  libraryUnavailableBody: 'Charts open from their links; settings are still reachable.',
  openSettings: 'Settings',
  dropTitle: 'Drop to open',
  dropBody: 'Songs, charts, packages (.pez, .zip), skins and pictures',
  unknownError: 'unknown error',

  // ---- keyboard check page `#/dev/keys` (W3-01; human checklist H3) ----
  matrixTitle: 'Keyboard check',
  matrixIntro:
    'Press each shortcut when it is shown. The page records whether this browser and operating system let it through and whether the browser kept its own action from happening. Some shortcuts close or switch the window; come back to this tab and go on. Download the result and attach it to the checklist.',
  matrixStart: 'Start',
  matrixContinue: 'Continue',
  matrixSkip: 'Skip this one',
  matrixRestart: 'Start over',
  matrixCopy: 'Copy the result',
  matrixDownload: 'Download the result',
  matrixCopied: 'The result is on the clipboard.',
  matrixCopyFailed: 'The browser did not allow copying. Download the result instead.',
  matrixProgress: '{done} of {total} tried',
  matrixPress: 'Press now:',
  matrixWaiting: 'If nothing happens within {sec} seconds, it counts as not let through.',
  matrixDone: 'Every shortcut was tried. Download the result.',
  matrixColChord: 'Shortcut',
  matrixColFrom: 'Bound in',
  matrixColCommands: 'Commands',
  matrixColResult: 'Result',
  matrixSource:
    '{source, select, default {Default keys} rpe {RPE keys} altLetter {Alt + letter} reserved {Reserved} reservedMac {Reserved on macOS} other {{source}}}',
  matrixResult:
    '{result, select, works {Works} missing {Not let through} kept {Browser acts on it too} blurred {Window lost focus} untested {Not tried yet} other {{result}}}',
  matrixFlags: 'Flags',
  matrixUnreliable: 'Listed as unreliable',
  matrixReserved: 'Reserved',
  matrixLabel: 'Keyboard check results',
});

export default catalogOf(m);
